/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getIcon,
  isEmptyOrNull,
  SLRLabelIcon,
  SLRModal,
  SLRPageHead,
  SLRSpinner,
  SLRTable
} from '@SLR/shared-library';
import { SolutionInfo, SOLUTIONS } from 'configs/solutions';
import { useGetSolutionConsents } from 'hooks';
import { Empty } from 'pages/organizations';
import { FC, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import './SolutionsPage.scss';

export const SolutionsPage: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'solutionsPage'
  });
  const { data: consents, isPending } = useGetSolutionConsents();

  const solutions: SolutionInfo[] = useMemo(() => {
    const solutionConsents = Object.keys(SOLUTIONS).map((key) => {
      const match = consents?.find(
        ({ applicationId }) => applicationId === key
      );
      if (match) return { ...SOLUTIONS[key], signUp: match.createdDate };
    });

    return solutionConsents.filter((s) => s !== undefined);
  }, [consents]);

  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const handleShowInfoModal = (show: boolean) => setShowInfoModal(show);

  if (isPending) {
    return <SLRSpinner />;
  }

  return (
    <>
      {showInfoModal && (
        <SLRModal
          isOpen
          size="lg"
          fullscreen="sm-down"
          className="solutions-page-info"
          title={t('info.title')}
          onClose={() => handleShowInfoModal(false)}
        >
          <h2 className="pb-3">{t('info.content.title')}</h2>

          <p>
            <Trans t={t} i18nKey="info.content.message" />
          </p>
        </SLRModal>
      )}

      <Container className="solutions-page table-responsive-md">
        <SLRPageHead
          className="pb-5"
          labels={{
            title: (
              <div className="d-flex flex-row gap-3">
                <span className="solutions-title">
                  {t('connectedSolutions')}
                </span>
                <SLRLabelIcon
                  width="36"
                  icon={getIcon('fal', 'info-circle')}
                  onClick={() => handleShowInfoModal(true)}
                />
              </div>
            ),
            description: t('description')
          }}
        />
        <Row>
          <Col xs={12}>
            <SLRTable<SolutionInfo>
              emptyView={<Empty labels={t('empty', { returnObjects: true })} />}
              sortIcon={getIcon('fal', 'long-arrow-up')}
              header={{ title: t('solutions') }}
              initialSort={{
                accessor: 'name',
                sortOrder: 'asc'
              }}
              columns={[
                {
                  label: t('solutions'),
                  accessor: 'name',
                  sortable: true
                },
                {
                  label: t('date'),
                  accessor: 'signUp',
                  sortable: true,
                  cellFn: ({ value }: { value: number }) =>
                    new Date(value).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })
                },
                {
                  label: t('toSolution'),
                  accessor: 'url',
                  sortable: false,
                  centered: true,
                  // eslint-disable-next-line react/no-unused-prop-types
                  cellFn: ({ value }: { value: string }) =>
                    isEmptyOrNull(value) ? (
                      <span>—</span>
                    ) : (
                      <Link to={value} target="_blank">
                        <FontAwesomeIcon
                          size="lg"
                          icon={getIcon('fal', 'up-right-from-square')}
                        />
                      </Link>
                    )
                }
              ]}
              data={solutions}
              responsive="md"
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};
